import { DateOnly } from '@microsoft/kiota-abstractions';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  BusinessRule,
  BusinessRuleTestRequest,
  BusinessRuleTestResponse,
  BusinessRuleUpdateRequest,
  CustomBusinessRuleCreateRequest,
} from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';

type DateInput = Date | DateOnly | string | null | undefined;

type LegacyBusinessRule = Omit<BusinessRule, 'fromDate' | 'toDate'> & {
  delete?: boolean;
  fromDate?: string | null;
  toDate?: string | null;
};

type LegacyBusinessRulePayload = Partial<LegacyBusinessRule> & Record<string, unknown>;

type LegacyBusinessRuleTestRequest = Omit<BusinessRuleTestRequest, 'flightDate' | 'requestDate'> & {
  flightDate?: DateInput;
  requestDate?: DateInput;
};

type LegacyBusinessRuleTestResult = {
  status: number;
  data: LegacyBusinessRuleTestResponse | undefined;
};

type LegacyBusinessRuleTestResponse = Omit<BusinessRuleTestResponse, 'testedRules'> & {
  testedRules?: LegacyBusinessRule[] | null;
};

function toDateOnly(value: DateInput): DateOnly | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return null;
  }

  if (value instanceof DateOnly) {
    return value;
  }

  if (value instanceof Date) {
    return DateOnly.fromDate(value);
  }

  if (typeof value === 'string') {
    return DateOnly.parse(value) ?? null;
  }

  return undefined;
}

function toBoolean(value: unknown, fallback?: boolean): boolean | null | undefined {
  if (value === undefined) {
    return fallback;
  }

  if (value === null || value === '') {
    return null;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();

    if (normalizedValue === 'true') {
      return true;
    }

    if (normalizedValue === 'false') {
      return false;
    }
  }

  return Boolean(value);
}

function normalizeRule(rule: Partial<BusinessRule> | null | undefined): LegacyBusinessRule | undefined {
  if (!rule) {
    return undefined;
  }

  return {
    ...rule,
    fromDate: rule.fromDate?.toString() ?? null,
    toDate: rule.toDate?.toString() ?? null,
  };
}

function normalizeRules(rules: BusinessRule[] | undefined): LegacyBusinessRule[] {
  const source = rules && rules.length ? rules : [];

  return source.map(rule => ({
    ...(normalizeRule(rule) ?? {}),
    delete: true,
  }));
}

function normalizeTestResponse(response: BusinessRuleTestResponse | undefined): LegacyBusinessRuleTestResponse | undefined {
  if (!response) {
    return undefined;
  }

  return {
    ...response,
    testedRules: response.testedRules?.map(rule => normalizeRule(rule) ?? {}) ?? [],
  };
}

function buildCreateRequest(data: LegacyBusinessRulePayload): CustomBusinessRuleCreateRequest {
  return {
    ...data,
    decreaseAllowedAfterIncrease: toBoolean(data.decreaseAllowedAfterIncrease),
    fromDate: toDateOnly(data.fromDate as DateInput),
    isFallbackToPrevious: toBoolean(data.isFallbackToPrevious, false) ?? false,
    toDate: toDateOnly(data.toDate as DateInput),
  } as CustomBusinessRuleCreateRequest;
}

function buildUpdateRequest(data: LegacyBusinessRulePayload): BusinessRuleUpdateRequest {
  return {
    ...data,
    decreaseAllowedAfterIncrease: toBoolean(data.decreaseAllowedAfterIncrease),
    fromDate: toDateOnly(data.fromDate as DateInput),
    isFallbackToPrevious: toBoolean(data.isFallbackToPrevious),
    toDate: toDateOnly(data.toDate as DateInput),
  } as BusinessRuleUpdateRequest;
}

function buildTestRequest(data: LegacyBusinessRuleTestRequest): BusinessRuleTestRequest {
  return {
    ...data,
    flightDate: toDateOnly(data.flightDate),
    isAlreadyIncreased: toBoolean(data.isAlreadyIncreased, false) ?? false,
    requestDate: toDateOnly(data.requestDate),
  } as BusinessRuleTestRequest;
}

export const useBusinessRulesStore = defineStore('businessRules', () => {
  const apiClient = useApiClient();

  const list = ref<LegacyBusinessRule[]>([]);
  const loading = ref<boolean>(true);

  async function add(data: LegacyBusinessRulePayload): Promise<LegacyBusinessRule | undefined> {
    loading.value = true;

    try {
      const response = await apiClient.businessRule.post(buildCreateRequest(data));
      return normalizeRule(response);
    } finally {
      loading.value = false;
    }
  }

  async function deleteBusinessRule({ id }: { id: number }): Promise<ArrayBuffer | undefined> {
    loading.value = true;

    try {
      return await apiClient.businessRule.delete({
        queryParameters: {
          id,
        },
      });
    } finally {
      loading.value = false;
    }
  }

  async function update(data: LegacyBusinessRulePayload): Promise<ArrayBuffer | undefined> {
    loading.value = true;

    try {
      return await apiClient.businessRule.put(buildUpdateRequest(data));
    } finally {
      loading.value = false;
    }
  }

  async function test(data: LegacyBusinessRuleTestRequest): Promise<LegacyBusinessRuleTestResult> {
    const response = await apiClient.businessRule.test.post(buildTestRequest(data));

    return {
      status: 200,
      data: normalizeTestResponse(response),
    };
  }

  async function get(): Promise<LegacyBusinessRule[]> {
    loading.value = true;

    try {
      const response = await apiClient.businessRule.get();
      list.value = normalizeRules(response);
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    add,
    delete: deleteBusinessRule,
    get,
    list,
    loading,
    test,
    update,
  };
});
